import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  AnyOperationState,
  OperationInitialState,
  OperationObserverService,
} from '@x/common/operation';
import { AuthService } from '@x/ecommerce-shop/app/core/auth/services/auth.service';
import { IconSize } from '@x/ecommerce-shop/app/core/shop-ui/components/icon-ui/icon-size.directive';
import { ButtonSizes } from '@x/ecommerce-shop/app/core/utils/directives/button-size.directive';
import {
  WishlistAddActionToQueue,
  WishlistAddToProductIds,
  WishlistRemoveFromProductIds,
  WishlistTempItem,
} from '@x/ecommerce-shop/app/core/whishlist/state/wishlist.actions';
import { WishlistState } from '@x/ecommerce-shop/app/core/whishlist/state/wishlist.state';
import { TShopOrderItemContext } from '@x/ecommerce/shop-client';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'x-wishlist-action',
  templateUrl: './wishlist-action.component.html',
  styleUrls: ['./wishlist-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-wishlist-action' },
})
export class WishlistActionComponent {
  @Input() productId: number;
  @Input() iconSize: IconSize = 'x-large';
  @Input() buttonSize: ButtonSizes = 'normal';
  @Input() context: TShopOrderItemContext;

  isInWishlist$: Observable<boolean> = this.store
    .select(WishlistState.wishlistProductIds)
    .pipe(map((ids) => ids.includes(this.productId)));

  private _q = this.operationService.createQueue();
  operationState$ = this._q.observe().pipe(
    map(({ state }) => state),
    startWith(new OperationInitialState()),
  );

  constructor(
    private operationService: OperationObserverService,
    private authService: AuthService,
    private router: Router,
    private store: Store,
  ) {}

  action(action: 'add' | 'remove', operation: AnyOperationState) {
    if (operation.status === 'loading') return;

    if (!this.authService.userId) {
      this.store.dispatch(new WishlistTempItem(this.productId));
      this.auth();
      return;
    }

    if (action === 'remove') {
      this._q.queue(
        this.store.dispatch(
          new WishlistAddActionToQueue(new WishlistRemoveFromProductIds(this.productId)),
        ),
      );
      return;
    }

    this._q.queue(
      this.store.dispatch(
        new WishlistAddActionToQueue(new WishlistAddToProductIds(this.productId, this.context)),
      ),
    );
  }

  private auth() {
    const redirect = this.router.url.split('?')[0];
    this.router.navigate(['/login'], {
      queryParams: { redirect },
      queryParamsHandling: 'merge',
    });
  }
}
